/**
 * The companion's state, in words: the Music app's status line, and the phone's status bar.
 */
import type { CompanionState } from './useAudioCompanion';

export interface CompanionStatus {
  readonly tone: 'live' | 'idle' | 'warn' | 'bad';
  readonly label: string;
  readonly detail: string;
}

/** The one sentence at the top of the player card, from the socket and the last context. */
export function statusOf(state: Pick<CompanionState, 'reloadRequired' | 'link' | 'heard' | 'context'>): CompanionStatus {
  if (state.reloadRequired) return { tone: 'bad', label: 'A new version is out', detail: 'Reload the page to keep listening.' };
  switch (state.link) {
    case 'idle':
    case 'connecting':
      return { tone: 'idle', label: 'Connecting…', detail: 'Finding your place in the game.' };
    case 'waiting':
      return { tone: 'warn', label: 'Reconnecting…', detail: 'The connection dropped. Trying again — the music keeps playing meanwhile.' };
    case 'stopped':
      return { tone: 'bad', label: 'Disconnected', detail: 'Reload the page to reconnect.' };
    case 'open':
      if (!state.heard) return { tone: 'idle', label: 'Waiting for the game server…', detail: 'Connected. The game server will say where you are in a moment.' };
      if (state.context?.online === true) return { tone: 'live', label: 'In game', detail: 'Your soundtrack follows you as you play.' };
      return { tone: 'idle', label: 'Not in game', detail: 'Join the server and your soundtrack starts here.' };
  }
}

/** The look of the phone around the music: the hub by day, by night (the Nether, the End), or a battle. */
export type Mood = 'day' | 'night' | 'battle';

export function moodOf(state: Pick<CompanionState, 'snapshot' | 'context'>): Mood {
  if (state.snapshot.area?.category === 'battle') return 'battle';
  return /nether|the_end/.test(state.context?.context?.dimension ?? '') ? 'night' : 'day';
}
